import ImageComponent from '@/src/components/common/ImageComponent'
import PriceComponent from '../PriceComponent'
import Link from 'next/link'
import { getSingleProductPath } from '@/src/routes/routes'
import { Button } from '@heroui/react'
import { CheckIconSVG, ExclusiveSVG, HeartOutlineSVG, QuickBuySVG } from '@/src/ui/AllSVG'
import { useDispatch } from 'react-redux'
import { openModal } from '@/src/store/slices/allModalSlice'
import type { INewArrivalsAPIResponseDataEntity } from '@/src/types/newArrivals'
import { useState } from 'react'
import AddToWishlist from '../../page-components/products/AddToWishlist'

interface ProductCardProps {
  data: INewArrivalsAPIResponseDataEntity
}

const MAX_MOBILE_COLORS = 1
const MAX_DESKTOP_COLORS = 3

export default function ProductCard({ data }: ProductCardProps) {
  const dispatch = useDispatch()
  const [selectedColor, setSelectedColor] = useState<string | null>(data?.variants?.[0]?.color ?? null)

  // Derived from selectedColor on every render — always in sync, never stale
  const variant = data?.variants?.find((v) => v.color === selectedColor) ?? data?.variants?.[0] ?? null
  const isVariant = data?.is_variant || Boolean(variant)
  const priceSource = isVariant ? variant : data?.variant
  const thumbnailSrc = isVariant ? (variant?.images?.[0] ?? data?.images?.[0] ?? '') : (data?.thumbnail ?? '')
  const galleryFirst = isVariant ? (variant?.images?.[1] ?? data?.images?.[1] ?? '') : (data?.images?.[1] ?? '')
  const hasHoverGallery = Boolean(galleryFirst) && galleryFirst !== thumbnailSrc

  const handleQuickBuyOpen = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dispatch(openModal({
      componentName: "ProductQuickViewModal",
      data: data ?? null,
      modalSize: "5xl",
      hideCloseButton: false,
      modalPadding: "p-0",
    }))
  }

  const handleColorSelect = (e: React.MouseEvent, colorName: string) => {
    e.preventDefault()
    e.stopPropagation()
    setSelectedColor(colorName)
  }


  return (
    <>
      <div className='space-y-4 cursor-pointer group'>
        {/* Image area */}
        <div className="relative aspect-4/5 w-full bg-secondary-background-color rounded-2xl overflow-hidden">
          <Link href={getSingleProductPath(data?.slug?.toString() ?? "")} prefetch={true} className="absolute inset-0 z-0" />

          {/* Category badge */}
          <span className={`absolute top-3 left-3 text-xs bg-white/40 px-2 py-1 rounded-sm font-medium z-10 transition-opacity duration-200 ${data?.exclusive ? 'opacity-0 group-hover:opacity-100' : ''}`}>
            {data?.categoryId?.name}
          </span>
          {data?.exclusive && <div className="absolute top-3 left-3 z-10 transition-opacity duration-200 group-hover:opacity-0"><ExclusiveSVG /></div>}

          {/* Preload all other variant images so color switching is instant */}
          <div className="hidden" aria-hidden="true">
            {data?.variants?.map((v) =>
              v.color !== selectedColor
                ? (v.images ?? []).slice(0, 2).map((imgSrc, i) =>
                  imgSrc ? (
                    <ImageComponent
                      key={`preload-${v.color}-${i}`}
                      src={imgSrc}
                      alt=""
                      object_cover={false}
                      loading="eager"
                    />
                  ) : null
                )
                : null
            )}
          </div>

          {/* Product image — painted above Link; pointer-events-none so the overlay Link receives clicks */}
          <div className="relative w-full h-full pointer-events-none">
            <div
              className={`absolute inset-0 transition-opacity duration-300 ease-out ${hasHoverGallery ? 'group-hover:opacity-0' : ''}`}
            >
              <ImageComponent
                src={thumbnailSrc}
                alt={data?.title}
                object_cover={false}
                priority={true}
              />
            </div>
            {hasHoverGallery && galleryFirst ? (
              <div className="absolute inset-0 opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100 pointer-events-none">
                <ImageComponent
                  src={galleryFirst}
                  alt={`${data?.title ?? 'Product'} — gallery`}
                  object_cover={false}
                  priority={true}
                />
              </div>
            ) : null}
          </div>

          {/* Wishlist button — top-right, visible on hover */}
          <div className="wishlist_btn absolute top-3 right-3 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          >
            <AddToWishlist product_id={data?._id} isAlreadyWishlist={data?.alreadyWishlist} />
          </div>

          {/* Quick Buy button — bottom-center, visible on hover; hidden on mobile */}
          <div className="hidden lg:block absolute bottom-4 left-1/2 -translate-x-1/2 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <Button
              className="global_btn rounded_full bg_white shadow-card-btn whitespace-nowrap"
              onClick={handleQuickBuyOpen}
            >
              <QuickBuySVG /> Quick Buy
            </Button>
          </div>
        </div>

        {/* Product info */}
        <div className="space-y-2">
          <Link href={getSingleProductPath(data?.slug?.toString() ?? "")}>
            <div className="flex flex-wrap lg:flex-nowrap items-baseline justify-between gap-x-4 gap-y-2">
              <h3 className="font-medium w-full truncate">{data?.title}</h3>
              <div className="w-full space-y-1 text-xs xl:text-sm lg:text-end">
                <p><PriceComponent title="Retail Price" lineThrough={true} currency={data?.pricing_context?.currency} price={priceSource?.rental_price ?? 0} /></p>
                <PriceComponent title="Our Price" currency={data?.pricing_context?.currency} price={priceSource?.price ?? 0} />
              </div>
            </div>
          </Link>
          <div className="flex flex-wrap flex-row-reverse lg:flex-row lg:flex-nowrap items-center justify-between gap-x-2 gap-y-2 text-xs xl:text-sm mt-2">
            {data?.colors && data?.colors?.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap">
                {data.colors.map((color, index) => {
                  const isSelected = selectedColor === color?.name
                  const hiddenOnMobile = index >= MAX_MOBILE_COLORS
                  const hiddenOnDesktop = index >= MAX_DESKTOP_COLORS
                  return (
                    <button
                      key={`${color?.code}-${index}`}
                      type="button"
                      title={color?.name}
                      aria-label={`Select ${color?.name}`}
                      aria-pressed={isSelected}
                      className={`relative w-6 h-6 shrink-0 hover:scale-110 transition-all duration-200 ease-out rounded-full border-3 border-white shadow-sm flex items-center justify-center ${isSelected ? 'scale-110 ring-2 ring-white ring-offset-1 ring-offset-muted-color' : ''} ${hiddenOnMobile ? 'max-md:hidden' : ''} ${hiddenOnDesktop ? 'md:hidden' : ''}`}
                      style={{ backgroundColor: color?.code }}
                      onClick={(e) => handleColorSelect(e, color?.name)}
                    >
                      {isSelected && (
                        <span className={`[&_svg_path]:fill-white [&_svg_path]:drop-shadow-sm ${color?.name?.toLowerCase() === 'white' ? '[&_svg_path]:fill-black!' : '[&_svg_path]:fill-white'}`}>
                          <CheckIconSVG />
                        </span>
                      )}
                    </button>
                  )
                })}
                {data.colors.length > MAX_MOBILE_COLORS && (
                  <Link
                    href={getSingleProductPath(data?.slug?.toString() ?? '')}
                    title={`${data.colors.length - MAX_MOBILE_COLORS} more colors`}
                    aria-label={`${data.colors.length - MAX_MOBILE_COLORS} more colors`}
                    className="flex md:hidden w-6 h-6 shrink-0 items-center justify-center rounded-full border-3 border-white shadow-sm bg-body-color/10 text-[10px] font-semibold text-body-color hover:scale-110 transition-all duration-200 ease-out"
                  >
                    +{data.colors.length - MAX_MOBILE_COLORS}
                  </Link>
                )}
                {data.colors.length > MAX_DESKTOP_COLORS && (
                  <Link
                    href={getSingleProductPath(data?.slug?.toString() ?? '')}
                    title={`${data.colors.length - MAX_DESKTOP_COLORS} more colors`}
                    aria-label={`${data.colors.length - MAX_DESKTOP_COLORS} more colors`}
                    className="hidden md:flex w-6 h-6 shrink-0 items-center justify-center rounded-full border-3 border-white shadow-sm bg-body-color/10 text-[10px] font-semibold text-body-color hover:scale-110 transition-all duration-200 ease-out"
                  >
                    +{data.colors.length - MAX_DESKTOP_COLORS}
                  </Link>
                )}
              </div>
            )}
            <Link href={getSingleProductPath(data?.slug?.toString() ?? "")}>
              <p className='bg-green-color text-white font-medium px-2 py-0.5 rounded-sm'><PriceComponent title="Members Price" currency={data?.pricing_context?.currency} price={priceSource?.membership_price ?? 0} /></p>
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}
